<template>
  <div class="forgot-password-page">
    <div class="forgot-password-container">
      <div class="forgot-password-card">
        <!-- Tenant Logo -->
        <div class="forgot-password-logo">
          <img
            v-if="tenantLogo"
            :src="tenantLogo"
            :alt="tenantName ?? ''"
            class="logo-image"
          />
          <div v-else class="logo-placeholder">
            {{ tenantName || 'Franchise Factory' }}
          </div>
        </div>

        <div class="forgot-password-header">
          <h1>{{ t('Forgot Password') }}</h1>
          <p class="subtitle">{{ t('Enter your username to receive a password reset code') }}</p>
        </div>

        <form @submit.prevent="handleForgotPassword" class="forgot-password-form">
          <!-- Error Message -->
          <div v-if="errorMessage" class="alert alert-error">
            {{ errorMessage }}
          </div>

          <!-- Success Message -->
          <div v-if="successMessage" class="alert alert-success">
            {{ successMessage }}
          </div>

          <!-- Username Field -->
          <div class="form-group">
            <label for="username">{{ t('Username') }}</label>
            <input
              id="username"
              v-model="username"
              type="text"
              :placeholder="t('Enter your username')"
              required
              autocomplete="username"
              :disabled="isLoading || resetCodeSent"
              data-testid="auth-forgot-username"
            />
          </div>

          <!-- Submit Button -->
          <button
            v-if="!resetCodeSent"
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading"
            data-testid="auth-forgot-submit"
          >
            <span v-if="!isLoading">{{ t('Send Reset Code') }}</span>
            <span v-else>{{ t('Sending...') }}</span>
          </button>

          <!-- Go to Reset Button -->
          <button
            v-else
            type="button"
            class="btn btn-success btn-block"
            @click="goToReset"
            data-testid="auth-forgot-go-to-reset"
          >
            {{ t('Continue to Reset Password') }}
          </button>
        </form>

        <!-- Back to Login Link -->
        <div class="forgot-password-footer">
          <p>
            {{ t('Remember your password?') }}
            <router-link to="/login" class="login-link">
              {{ t('Back to Login') }}
            </router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { tenantConfig } from '@/services/tenantConfigService'
import { log } from '@/utils/DebugLogger'

const { t } = useI18n()
const router = useRouter()
const { forgotPassword } = useAuth()

// Tenant branding
const tenantLogo = computed<string | null>(() => tenantConfig.get<string>('logo_url'))
const tenantName = computed<string | null>(() => tenantConfig.get<string>('tenant_name'))
const primaryColor = computed<string>(() => tenantConfig.get<string>('primary_color') || '#667eea')
const secondaryColor = computed<string>(() => tenantConfig.get<string>('secondary_color') || '#764ba2')

// Form state
const username = ref<string>('')
const isLoading = ref<boolean>(false)
const errorMessage = ref<string>('')
const successMessage = ref<string>('')
const resetCodeSent = ref<boolean>(false)

onMounted(() => {
  log('ForgotPasswordPage', 'onMounted', 'Forgot password page loaded')
})

/**
 * Handle forgot password form submission
 */
async function handleForgotPassword(): Promise<void> {
  try {
    isLoading.value = true
    errorMessage.value = ''
    successMessage.value = ''

    log('ForgotPasswordPage', 'handleForgotPassword', 'Requesting password reset', {
      username: username.value
    })

    const result = await forgotPassword(username.value)

    if (result.success) {
      log('ForgotPasswordPage', 'handleForgotPassword', '✅ Reset code sent successfully')

      resetCodeSent.value = true
      successMessage.value = t('Password reset code sent! Check your email.')
    } else {
      log('ForgotPasswordPage', 'handleForgotPassword', '❌ Request failed:', result.error)
      errorMessage.value = result.error || t('Failed to send reset code')
    }
  } catch (error) {
    log('ForgotPasswordPage', 'handleForgotPassword', '❌ Request error:', error)
    errorMessage.value = t('An error occurred. Please try again.')
  } finally {
    isLoading.value = false
  }
}

/**
 * Navigate to reset password page
 */
function goToReset(): void {
  router.push({
    path: '/reset-password',
    query: { username: username.value }
  })
}
</script>

<style scoped>
.forgot-password-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: v-bind(primaryColor);
  padding: 1rem;
}

.forgot-password-container {
  width: 100%;
  max-width: 420px;
}

.forgot-password-card {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.forgot-password-logo {
  text-align: center;
  margin-bottom: 1.5rem;
}

.logo-image {
  max-width: 180px;
  max-height: 80px;
  height: auto;
  object-fit: contain;
}

.logo-placeholder {
  font-size: 1.5rem;
  font-weight: 700;
  color: v-bind(primaryColor);
  padding: 1rem;
}

.forgot-password-header {
  text-align: center;
  margin-bottom: 2rem;
}

.forgot-password-header h1 {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #718096;
  font-size: 0.875rem;
}

.forgot-password-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: #2d3748;
  font-size: 0.875rem;
}

.form-group input {
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: v-bind(primaryColor);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-group input:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
}

.btn {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: v-bind(secondaryColor);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-success {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
}

.btn-success:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
}

.alert {
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
}

.alert-error {
  background-color: #fed7d7;
  color: #c53030;
  border: 1px solid #fc8181;
}

.alert-success {
  background-color: #c6f6d5;
  color: #22543d;
  border: 1px solid #68d391;
}

.forgot-password-footer {
  margin-top: 1.5rem;
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.forgot-password-footer p {
  color: #718096;
  font-size: 0.875rem;
}

.login-link {
  color: v-bind(primaryColor);
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s;
}

.login-link:hover {
  color: v-bind(secondaryColor);
  text-decoration: underline;
}
</style>
